import * as THREE from 'three';
import { SurfaceType } from '../terrain/SurfaceType';

export interface BallStateSnapshot {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  spin: THREE.Vector3;
  surfaceType: SurfaceType;
  altitude: number;
  isResting: boolean;
  phase: BallPhase;
  motionMode: BallMotionMode;
}

export type BallPhase = 'flying' | 'bouncing' | 'rolling' | 'resting';
export type BallMotionMode = 'airborneShot' | 'landingRoll' | 'putt' | 'genericRoll' | 'resting';

/** Mutable runtime state for one golf ball. Kept renderer-agnostic so it can be simulated, tested, replayed, or networked later. */
export class BallState {
  readonly position = new THREE.Vector3();
  readonly velocity = new THREE.Vector3();
  readonly spin = new THREE.Vector3();

  surfaceType = SurfaceType.Rough;
  altitude = 0;
  isResting = true;
  phase: BallPhase = 'resting';
  motionMode: BallMotionMode = 'resting';
  bounceCount = 0;
  rolloutResistanceMultiplier = 1;
  bounceRestitutionMultiplier = 1;

  constructor(initialPosition = new THREE.Vector3()) {
    this.position.copy(initialPosition);
  }

  setLaunch(
    velocity: THREE.Vector3,
    spin = new THREE.Vector3(),
    options: {
      readonly rolloutResistanceMultiplier?: number;
      readonly bounceRestitutionMultiplier?: number;
    } = {},
  ): void {
    this.velocity.copy(velocity);
    this.spin.copy(spin);
    this.rolloutResistanceMultiplier = options.rolloutResistanceMultiplier ?? 1;
    this.bounceRestitutionMultiplier = options.bounceRestitutionMultiplier ?? 1;
    this.isResting = false;
    this.phase = 'flying';
    this.motionMode = 'airborneShot';
    this.bounceCount = 0;
  }

  setRolling(
    velocity: THREE.Vector3,
    spin = new THREE.Vector3(),
    motionMode: BallMotionMode = 'genericRoll',
  ): void {
    this.velocity.copy(velocity);
    this.spin.copy(spin);
    this.rolloutResistanceMultiplier = 1;
    this.bounceRestitutionMultiplier = 0;
    this.isResting = false;
    this.phase = 'rolling';
    this.motionMode = motionMode;
    this.bounceCount = 0;
  }

  reset(position: THREE.Vector3): void {
    this.position.copy(position);
    this.velocity.set(0, 0, 0);
    this.spin.set(0, 0, 0);
    this.altitude = 0;
    this.isResting = true;
    this.phase = 'resting';
    this.motionMode = 'resting';
    this.bounceCount = 0;
    this.rolloutResistanceMultiplier = 1;
    this.bounceRestitutionMultiplier = 1;
  }

  snapshot(): BallStateSnapshot {
    return {
      position: this.position.clone(),
      velocity: this.velocity.clone(),
      spin: this.spin.clone(),
      surfaceType: this.surfaceType,
      altitude: this.altitude,
      isResting: this.isResting,
      phase: this.phase,
      motionMode: this.motionMode,
    };
  }
}
